import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import KPICardWithTrend from "../KPICardWithTrend";
import EnrollmentTrendChart from "./EnrollmentTrendChart";
import ProgramDistributionChart from "./ProgramDistributionChart";
import styles from "./PatientEnrollment.module.css";
import MonthYearPicker from "../../shared-modules/DatePicker/MonthYearPicker";
import {
  getEnrollmentTrend,
  getPatientEnrollmentKpis,
  getProgramDistribution,
} from "../../../services/super-admin-dashboard/patientEnrollment";
import { buildMonthAwareTitle, getDashboardMonthContext } from "../kpiLabelUtils";

const PatientEnrollment = ({ scope = "all" }) => {
  const [selectedMonthYear, setSelectedMonthYear] = useState("");
  const { hasMonthFilter, monthLabel } = getDashboardMonthContext(selectedMonthYear);

  const { data: kpis, isLoading } = useQuery({
    queryKey: ["patient-enrollment-kpis", scope, selectedMonthYear],
    queryFn: () => getPatientEnrollmentKpis(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const { data: enrollmentTrend, isLoading: isEnrollmentTrendLoading } = useQuery({
    queryKey: ["patient-enrollment-trend", scope, selectedMonthYear],
    queryFn: () => getEnrollmentTrend(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const { data: programDistribution, isLoading: isProgramDistributionLoading } = useQuery({
    queryKey: ["patient-enrollment-program-distribution", scope, selectedMonthYear],
    queryFn: () => getProgramDistribution(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const programDistributionData = [
    {
      name: "RPM Only",
      value: programDistribution?.rpm_only ?? 0,
      pct: programDistribution?.rpm_only_pct ?? 0,
      total: programDistribution?.total ?? 0,
    },
    {
      name: "CCM Only",
      value: programDistribution?.ccm_only ?? 0,
      pct: programDistribution?.ccm_only_pct ?? 0,
      total: programDistribution?.total ?? 0,
    },
    {
      name: "RPM + CCM",
      value: programDistribution?.rpm_ccm ?? 0,
      pct: programDistribution?.rpm_ccm_pct ?? 0,
      total: programDistribution?.total ?? 0,
    },
  ];

  const m = hasMonthFilter ? (
    <span className={styles.monthBadge}>(upto {monthLabel})</span>
  ) : null;

  const onlyMonth = hasMonthFilter ? (
    <span className={styles.monthBadge}>({monthLabel})</span>
  ) : null;

  const consentCoverageTitle = buildMonthAwareTitle({
    hasMonthFilter,
    monthLabel,
    defaultLabel: "Consent Form Coverage",
    filteredLabel: "Consent Form Coverage",
    showMonthWhenUnfiltered: true,
    monthClassName: styles.monthBadge,
  });

  const documentCoverageTitle = buildMonthAwareTitle({
    hasMonthFilter,
    monthLabel,
    defaultLabel: "Document Coverage",
    showMonthWhenUnfiltered: true,
    monthClassName: styles.monthBadge,
  });

  const cardTitles = hasMonthFilter
    ? {
        totalEnrolled: <>Total Enrolled {m}</>,
        activePatients: <>Total Active {m}</>,
        deactivated: <>Total Deactive {m}</>,
        newThisMonth: <>New This Month {onlyMonth}</>,
        consentCoverage: consentCoverageTitle,
        documentCoverage: documentCoverageTitle,
      }
    : {
        totalEnrolled: "Total Enrolled",
        activePatients: "Total Active",
        deactivated: "Total Deactive",
        newThisMonth: "New This Month",
        consentCoverage: consentCoverageTitle,
        documentCoverage: documentCoverageTitle,
      };

  const chartTitle = (base) =>
    hasMonthFilter ? <>{base} {onlyMonth}</> : base;

  return (
    <div className={styles.sectionCard}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Patient Enrollment</h2>
        <MonthYearPicker value={selectedMonthYear} onChange={setSelectedMonthYear} />
      </div>
      <div className={styles.divider} />

      <div className={styles.kpiGrid}>
        <KPICardWithTrend
          title={cardTitles.totalEnrolled}
          value={isLoading ? "..." : String(kpis?.total_enrolled ?? 0)}
          className={`${styles.kpiBlue} ${styles.kpiSmall}`}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={cardTitles.activePatients}
          value={isLoading ? "..." : String(kpis?.active_patients ?? 0)}
          className={styles.kpiGreen}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={cardTitles.deactivated}
          value={isLoading ? "..." : String(kpis?.discharged ?? 0)}
          className={styles.kpiAmber}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={cardTitles.newThisMonth}
          value={isLoading ? "..." : String(kpis?.new_this_month ?? 0)}
          className={styles.kpiIndigo}
          isLoading={isLoading}
        />

        <KPICardWithTrend
          title={cardTitles.consentCoverage}
          value={isLoading ? "..." : `${Number(kpis?.consent_form_coverage?.percentage ?? 0).toFixed(2)}%`}
          trend={isLoading ? null : `${kpis?.consent_form_coverage?.patients_with_consent ?? 0} patients`}
          className={`${styles.kpiIndigo} ${styles.kpiWideTall}`}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={cardTitles.documentCoverage}
          value={isLoading ? "..." : `${Number(kpis?.document_coverage?.percentage ?? 0).toFixed(2)}%`}
          trend={isLoading ? null : `${kpis?.document_coverage?.patients_with_docs ?? 0} patients`}
          className={`${styles.kpiBlue} ${styles.kpiWideTall}`}
          isLoading={isLoading}
        />
      </div>

      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <EnrollmentTrendChart
            data={enrollmentTrend || []}
            title={chartTitle("Enrollment Trend")}
            enrolledLabel="Enrolled"
            isLoading={isEnrollmentTrendLoading}
           
          />
        </div>
        <div className={styles.chartCard}>
          <ProgramDistributionChart
            data={programDistributionData}
            title={chartTitle("Program Distribution")}
            isLoading={isProgramDistributionLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default PatientEnrollment;